import { useNavigate, useParams } from "react-router-dom";

type FlowStep<V extends string> = {
  value: V;
  canEnter?: (state: Record<V, boolean>) => boolean;
};

type UseStepFlowParams<S extends { value: string }> = {
  steps: (S & FlowStep<S["value"]>)[];
  validityState: [Record<S["value"], boolean>, (value: Record<S["value"], boolean>) => void];
};

export default function useStepFlow<S extends { value: string }>({ steps, validityState }: UseStepFlowParams<S>) {
  type V = S["value"];
  const [validity, setValidity] = validityState;
  const { step: stepParam } = useParams();
  const navigate = useNavigate();
  const currentStepIndex = Math.max(
    steps.findIndex((s) => s.value === stepParam),
    0,
  );
  const currentStepConfig = steps[currentStepIndex];
  const currentStep = currentStepConfig.value;

  const indexOfStep = (step: V) => steps.findIndex((s) => s.value === step);

  const navigateToStep = (step: V, replace = false) => {
    if (indexOfStep(step) < 0) return;
    navigate(stepParam ? `../${step}` : step, { relative: "path", replace });
  };

  const canEnterStep = (step: V, overrideValidity?: Record<V, boolean>): boolean => {
    const config = steps.find((s) => s.value === step);
    return config?.canEnter?.(overrideValidity ?? validity) ?? false;
  };

  const goToStep = (step: V) => {
    if (!canEnterStep(step) || step === currentStep) return;
    navigateToStep(step);
  };

  const validateStep = (step: V) => {
    const newValidity = {
      ...validity,
      [step]: true,
    };
    setValidity(newValidity);
    return newValidity;
  };
  const invalidateStep = (step: V) => {
    const newValidity = {
      ...validity,
      [step]: false,
    };
    setValidity(newValidity);
    return newValidity;
  };

  const goToNextStep = (overrideValidity?: Record<V, boolean>) => {
    console.log(overrideValidity);
    const nextStep = steps[currentStepIndex + 1];
    if (!nextStep) return;
    if (!canEnterStep(nextStep.value, overrideValidity)) return;
    navigateToStep(nextStep.value);
  };
  const goToPrevStep = () => {
    const prevStep = steps[currentStepIndex - 1];
    if (!prevStep) return;
    navigateToStep(prevStep.value);
  };

  const ensureStepAllowed = () => {
    if (!canEnterStep(currentStep)) navigateToStep(steps[0].value, true);
  };

  const isStepAllowed = (step: V) => canEnterStep(step);

  return {
    steps,
    currentStep,
    currentStepIndex,
    currentStepConfig,
    indexOfStep,
    goToStep,
    validateStep,
    invalidateStep,
    isStepAllowed,
    goToNextStep,
    goToPrevStep,
    ensureStepAllowed,
  };
}
